const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    /^127(?:.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/.test(
      window.location.hostname,
    ),
);

// When a new SW activates we need to reload to pick up the new assets, but we
// must never do it while the user is actively on the page — that wipes the
// in-memory Apollo cache and produces a white screen (especially common on
// iOS Safari, which activates new SWs far more aggressively than Chrome).
//
// Strategy: reload immediately only if the tab is already hidden (backgrounded).
// If the tab is visible, set a flag and reload the moment the user switches
// away (visibilitychange → hidden). Either way, zero user interaction required.
let pendingSwReload = false;

document.addEventListener('visibilitychange', () => {
  if (!pendingSwReload) return;
  // Fire on hidden (desktop) and on visible (iOS fallback: background reload
  // may have been suspended by WKWebView before completing).
  window.location.reload();
});

function scheduleReload() {
  if (document.visibilityState === 'hidden') {
    window.location.reload();
  } else {
    pendingSwReload = true;
  }
}

export async function register(config) {
  if (!('serviceWorker' in navigator)) return;

  const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
  if (publicUrl.origin !== window.location.origin) {
    return;
  }

  const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

  window.addEventListener('load', async () => {
    try {
      if (isLocalhost) {
        await checkValidServiceWorker(swUrl, config);
        await navigator.serviceWorker.ready;
        config?.onReady && config.onReady();
      } else {
        await registerValidSW(swUrl, config);
      }
    } catch (err) {
      console.error('Error during service worker registration:', err);
    }
  });
}

async function registerValidSW(swUrl, config) {
  let registration;
  try {
    registration = await navigator.serviceWorker.register(swUrl, {
      updateViaCache: 'none',
    });
  } catch (err) {
    console.error('[SW] Registration failed:', err);
    return;
  }

  const hadController = !!navigator.serviceWorker.controller;
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController) return;
    if (reloading) return;
    reloading = true;
    scheduleReload();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      registration.update().catch(() => {});
    }
  });

  registration.addEventListener('updatefound', () => {
    const installing = registration.installing;
    if (!installing) return;

    installing.addEventListener('statechange', () => {
      if (
        installing.state === 'installed' &&
        navigator.serviceWorker.controller
      ) {
        config?.onUpdate && config.onUpdate(registration);
      } else if (installing.state === 'installed') {
        config?.onSuccess && config.onSuccess(registration);
      }
    });
  });

  return registration;
}

async function checkValidServiceWorker(swUrl, config) {
  try {
    const response = await fetch(swUrl, {
      cache: 'no-cache',
      headers: { 'Service-Worker': 'script' },
    });
    const contentType = response.headers.get('content-type') || '';
    if (response.status === 404 || !contentType.includes('javascript')) {
      const registration = await navigator.serviceWorker.ready;
      await registration.unregister();
      window.location.reload();
    } else {
      await registerValidSW(swUrl, config);
    }
  } catch {
    console.log(
      'No internet connection found. App is running in offline mode.',
    );
  }
}

export function unregister() {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.ready
    .then((registration) => registration.unregister())
    .catch((error) => console.error(error.message));
}

export async function applyServiceWorkerUpdate() {
  if (!('serviceWorker' in navigator)) return;
  const reg = await navigator.serviceWorker.getRegistration();
  if (reg?.waiting) {
    reg.waiting.postMessage({ type: 'SKIP_WAITING' });
  }
}

export async function checkForServiceWorkerUpdate() {
  if (!('serviceWorker' in navigator)) return;
  const reg = await navigator.serviceWorker.getRegistration();
  await reg?.update();
}
